import { inject, Injectable, LOCALE_ID, signal } from '@angular/core';
import { DateRange } from '@shared/models/common.models';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

@Injectable({
  providedIn: 'root',
})
export class UserLocaleService {
  private readonly DEFAULT_LOCALE = inject(LOCALE_ID);

  private readonly _userLocale = signal<string>(this.DEFAULT_LOCALE);
  public userLocale = this._userLocale.asReadonly();

  private readonly _userTimezone = signal<string>('UTC');
  public userTimezone = this._userTimezone.asReadonly();

  private readonly _userZoneOffset = signal<string>('Z');
  public userZoneOffset = this._userZoneOffset.asReadonly();

  public static readonly SUPPORTED_LOCALES = ['en', 'de', 'nl', 'fr', 'es', 'pt', 'da', 'pl', 'ar', 'tr', 'nb', 'zh', 'pt'];

  public static readonly LOCALE_IMPORT_MAP: { [key: string]: () => Promise<any> } = {
    //@ts-ignore
    en: () => import('@angular/common/locales/en'),
    //@ts-ignore
    de: () => import('@angular/common/locales/de'),
    //@ts-ignore
    nl: () => import('@angular/common/locales/nl'),
    //@ts-ignore
    fr: () => import('@angular/common/locales/fr'),
    //@ts-ignore
    es: () => import('@angular/common/locales/es'),
    //@ts-ignore
    pt: () => import('@angular/common/locales/pt'),
    //@ts-ignore
    da: () => import('@angular/common/locales/da'),
    //@ts-ignore
    pl: () => import('@angular/common/locales/pl'),
    //@ts-ignore
    ar: () => import('@angular/common/locales/ar'),
    //@ts-ignore
    tr: () => import('@angular/common/locales/tr'),
    //@ts-ignore
    nb: () => import('@angular/common/locales/nb'),
    //@ts-ignore
    zh: () => import('@angular/common/locales/zh'),
  };

  public setUserLocale(locale: string) {
    if (this.isLocaleAvailable(locale)) {
      this._userLocale.set(locale);
    }
  }

  public setUserTimezone(timezone: string) {
    this._userTimezone.set(timezone);
  }

  public setUserZoneOffset(offset: string) {
    this._userZoneOffset.set(offset);
  }

  private isLocaleAvailable(locale: string): boolean {
    return (
      Intl.NumberFormat.supportedLocalesOf([locale]).length > 0 &&
      UserLocaleService.SUPPORTED_LOCALES.includes(locale.split('-')[0])
    );
  }

  convertDateRangeToUserTimeZone(dateRange: DateRange): DateRange {
    const timezone = this.userTimezone();
    const pattern = "yyyy-MM-dd'T'HH:mm:ssXXX";
    return {
      startDate: formatInTimeZone(
        fromZonedTime(`${dateRange.startDate}T00:00:00`, timezone),
        timezone,
        pattern,
      ),
      endDate: formatInTimeZone(
        fromZonedTime(`${dateRange.endDate}T23:59:59`, timezone),
        timezone,
        pattern,
      ),
    };
  }
}
